import configs from "../configs";
import { getAuth0Token } from "../ui/utils";

export default class ScenesService {
  async getScene(fetch, id) {
    const token = await getAuth0Token();
    const url = `${configs.CUSTOM_API_URL}/metaverse-scenes/${id}`;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "application/json"
        }
      });

      if (!response.ok) {
        throw new Error(`Network Error: ${response.statusText}`);
      }

      const json = await response.json();

      return json.data ? json.data : json;
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  async getScenes(fetch, params) {
    const token = await getAuth0Token();
    const page = params?.page || 1;
    const perPage = params?.perPage || 1000;

    let url = `${configs.CUSTOM_API_URL}/metaverse-scenes?per_page=${perPage}&page=${page}`;

    if (params?.q) {
      url += `&q=${params.q}`;
    }

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "application/json"
        }
      });

      if (!response.ok) {
        throw new Error(`Network Error: ${response.statusText}`);
      }

      const json = await response.json();

      return json["data"];
    } catch (e) {
      console.error(e);
      return e.message;
    }
  }

  /**
   * Admin endpoint to get all metaverse scenes across the platform
   * @param {object} params - Query parameters
   * @param {number} params.page - Page number (default 1)
   * @param {number} params.perPage - Items per page (default 20, max 100)
   * @param {string} params.q - Search query (name, description, user)
   * @param {string} params.sortBy - Sort field (name, created_at, updated_at)
   * @param {string} params.sortOrder - Sort direction (asc, desc)
   * @param {number} params.userId - Filter by user ID
   * @param {boolean} params.isPublic - Filter by visibility
   * @param {boolean} params.hasRoom - Filter scenes with/without rooms
   */
  async getAllScenes(fetch, params) {
    const token = await getAuth0Token();

    const queryParams = new URLSearchParams();
    queryParams.set("page", params?.page || 1);
    queryParams.set("per_page", params?.perPage || 20);

    if (params?.q) queryParams.set("q", params.q);
    if (params?.sortBy) queryParams.set("sort_by", params.sortBy);
    if (params?.sortOrder) queryParams.set("sort_order", params.sortOrder);
    if (params?.userId) queryParams.set("user_id", params.userId);
    if (params?.isPublic !== undefined) queryParams.set("is_public", params.isPublic);
    if (params?.hasRoom !== undefined) queryParams.set("has_room", params.hasRoom);
    if (params?.published !== undefined) queryParams.set("published", params.published);

    const url = `${configs.CUSTOM_API_URL}/admin/metaverse-scenes?${queryParams.toString()}`;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "application/json"
        }
      });

      if (!response.ok) {
        throw new Error(`Network Error: ${response.statusText}`);
      }

      return response.json();
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  /**
   * Admin endpoint to delete any metaverse scene
   */
  async adminDeleteScene(fetch, { id, metaverseToken }) {
    const token = await getAuth0Token();
    const url = `${configs.CUSTOM_API_URL}/admin/metaverse-scenes/${id}`;

    try {
      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          "metaverse-token": metaverseToken
        }
      });

      if (!response.ok) {
        throw new Error(`Network Error: ${response.statusText}`);
      }

      return true;
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  async deleteScene(fetch, { id, metaverseToken }) {
    const token = await getAuth0Token();
    const url = `${configs.CUSTOM_API_URL}/metaverse-scenes/${id}`;

    try {
      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          "metaverse-token": metaverseToken
        }
      });

      if (!response.ok) {
        throw new Error(`Network Error: ${response.statusText}`);
      }

      return true;
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  async getSceneMetrics(fetch, slug, startDate, endDate, abortSignal = null) {
    const token = await getAuth0Token();
    const url = `${configs.CUSTOM_API_URL}/metaverse-rooms/${slug}/metrics?start_date=${startDate}&end_date=${endDate}`;

    const response = await fetch(url, {
      signal: abortSignal,
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        Accept: "application/json"
      }
    });

    if (!response.ok) {
      throw new Error(`Network Error: ${response.statusText}`);
    }

    const json = await response.json();

    return json;
  }

  async updateRoomVisibility(fetch, roomId, isPublic) {
    const token = await getAuth0Token();
    const url = `${configs.CUSTOM_API_URL}/metaverse-rooms/${roomId}/visibility`;

    try {
      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "application/json"
        },
        body: JSON.stringify({
          is_public: isPublic
        })
      });

      if (!response.ok) {
        throw new Error(`Network Error: ${response.statusText}`);
      }

      const json = await response.json();

      return json;
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  async makeRoomPublic(fetch, roomId) {
    return this.updateRoomVisibility(fetch, roomId, true);
  }

  async makeRoomPrivate(fetch, roomId) {
    return this.updateRoomVisibility(fetch, roomId, false);
  }

  async getPublicRooms(fetch) {
    const token = await getAuth0Token();
    const url = `${configs.CUSTOM_API_URL}/metaverse-rooms/public`;
    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json"
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    try {
      const response = await fetch(url, {
        method: "GET",
        headers
      });

      if (!response.ok) {
        throw new Error(`Network Error: ${response.statusText}`);
      }

      return await response.json();
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  async getRooms(fetch) {
    const token = await getAuth0Token();
    const url = `${configs.CUSTOM_API_URL}/user/metaverse-rooms`;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "application/json"
        }
      });

      if (!response.ok) {
        throw new Error(`Network Error: ${response.statusText}`);
      }

      const json = await response.json();

      return json["data"];
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  async deleteRoom(fetch, roomId) {
    const token = await getAuth0Token();
    const url = `${configs.CUSTOM_API_URL}/metaverse-rooms/${roomId}/delete`;

    try {
      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "application/json"
        }
      });

      if (!response.ok) {
        throw new Error(`Network Error: ${response.statusText}`);
      }

      return true;
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  /**
   * Admin endpoint to get all metaverse rooms across the platform
   * @param {object} params - Query parameters
   * @param {number} params.page - Page number (default 1)
   * @param {number} params.perPage - Items per page (default 20, max 100)
   * @param {string} params.q - Search query (name, description, user)
   * @param {string} params.sortBy - Sort field (name, created_at, updated_at)
   * @param {string} params.sortOrder - Sort direction (asc, desc)
   * @param {number} params.userId - Filter by user ID
   * @param {boolean} params.isPublic - Filter by visibility
   * @param {string} params.status - Filter by status
   * @param {string} params.sceneId - Filter by scene ID
   */
  async getAllRooms(fetch, params) {
    const token = await getAuth0Token();

    const queryParams = new URLSearchParams();
    queryParams.set("page", params?.page || 1);
    queryParams.set("per_page", params?.perPage || 20);

    if (params?.q) queryParams.set("q", params.q);
    if (params?.sortBy) queryParams.set("sort_by", params.sortBy);
    if (params?.sortOrder) queryParams.set("sort_order", params.sortOrder);
    if (params?.userId) queryParams.set("user_id", params.userId);
    if (params?.isPublic !== undefined) queryParams.set("is_public", params.isPublic);
    if (params?.status) queryParams.set("status", params.status);
    if (params?.sceneId) queryParams.set("scene_id", params.sceneId);
    if (params?.hidden !== undefined) queryParams.set("hidden", params.hidden);

    const url = `${configs.CUSTOM_API_URL}/admin/metaverse-rooms?${queryParams.toString()}`;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "application/json"
        }
      });

      if (!response.ok) {
        throw new Error(`Network Error: ${response.statusText}`);
      }

      return response.json();
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  /**
   * Admin endpoint to delete any metaverse room
   */
  async adminDeleteRoom(fetch, roomId) {
    const token = await getAuth0Token();
    const url = `${configs.CUSTOM_API_URL}/admin/metaverse-rooms/${roomId}`;

    try {
      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "application/json"
        }
      });

      if (!response.ok) {
        throw new Error(`Network Error: ${response.statusText}`);
      }

      return true;
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  /**
   * Assign a scene to multiple groups
   * @param {string} sceneId - The scene ID
   * @param {Array<number>} groupIds - Array of group IDs to assign
   * @param {string} groupPrivacy - Optional privacy setting
   */
  async assignSceneToGroups(fetch, sceneId, groupIds, groupPrivacy = null) {
    const token = await getAuth0Token();
    const url = `${configs.CUSTOM_API_URL}/metaverse-scenes/${sceneId}/user-groups`;

    const body = {
      user_group_ids: groupIds
    };

    if (groupPrivacy) {
      body.group_privacy = groupPrivacy;
    }

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "application/json"
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        throw new Error(`Network Error: ${response.statusText}`);
      }

      return response.json();
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  /**
   * Remove a scene from a group
   * @param {string} sceneId - The scene ID
   * @param {number} groupId - The group ID to remove
   */
  async removeSceneFromGroup(fetch, sceneId, groupId) {
    const token = await getAuth0Token();
    const url = `${configs.CUSTOM_API_URL}/metaverse-scenes/${sceneId}/user-groups/${groupId}`;

    try {
      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "application/json"
        }
      });

      if (!response.ok) {
        throw new Error(`Network Error: ${response.statusText}`);
      }

      return response.json();
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  /**
   * Get metaverse scenes for a specific user group
   * @param {object} fetch - Fetch function
   * @param {number|string} groupId - User group ID
   * @param {object} params - Query parameters
   * @param {number} params.page - Page number (default 1)
   * @param {number} params.perPage - Items per page (default 10)
   * @param {string} params.search - Search query
   */
  async getGroupScenes(fetch, groupId, params = {}) {
    const token = await getAuth0Token();
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.set("page", params.page);
    if (params.perPage) queryParams.set("perPage", params.perPage);
    if (params.search) queryParams.set("search", params.search);

    const url = `${configs.CUSTOM_API_URL}/user-groups/${groupId}/metaverse-scenes${
      queryParams.toString() ? `?${queryParams.toString()}` : ""
    }`;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "application/json"
        }
      });

      if (!response.ok) {
        throw new Error(`Network Error: ${response.statusText}`);
      }

      const json = await response.json();

      // Handle both paginated and non-paginated responses
      if (json.data) {
        return json;
      }

      return {
        data: Array.isArray(json) ? json : [],
        meta: {
          current_page: 1,
          last_page: 1,
          total: Array.isArray(json) ? json.length : 0
        }
      };
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  /**
   * Assign a scene to multiple workspaces.
   * @param {Function} fetch - Fetch function
   * @param {string} sceneId - The scene ID
   * @param {Array<string>} workspaceIds - Array of workspace IDs to assign
   * @param {Object} permissions - Optional permissions object
   */
  async assignSceneToWorkspaces(fetch, sceneId, workspaceIds, permissions = null) {
    const token = await getAuth0Token();
    const url = `${configs.CUSTOM_API_URL}/metaverse-scenes/${sceneId}/workspaces`;

    const body = {
      workspace_ids: workspaceIds
    };

    if (permissions) {
      body.permissions = permissions;
    }

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "application/json"
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        throw new Error(`Network Error: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error assigning scene to workspaces:", error);
      throw error;
    }
  }

  /**
   * Remove a scene from a workspace.
   * @param {Function} fetch - Fetch function
   * @param {string} sceneId - The scene ID
   * @param {string} workspaceId - The workspace ID to remove
   */
  async removeSceneFromWorkspace(fetch, sceneId, workspaceId) {
    const token = await getAuth0Token();
    const url = `${configs.CUSTOM_API_URL}/metaverse-scenes/${sceneId}/workspaces/${workspaceId}`;

    try {
      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "application/json"
        }
      });

      if (!response.ok) {
        throw new Error(`Network Error: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error removing scene from workspace:", error);
      throw error;
    }
  }
}
